import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Product } from '@/lib/products';

// Clear stale cart data from old format
try {
  const raw = localStorage.getItem('cynthia-cart');
  if (raw) {
    const parsed = JSON.parse(raw);
    const items = parsed?.state?.items;
    if (Array.isArray(items) && items.length > 0 && (items[0]?.variantId || items[0]?.purchaseMode)) {
      localStorage.removeItem('cynthia-cart');
    }
  }
} catch { /* ignore */ }

export interface CartItem {
  product: Product;
  quantity: number;
}

interface CartStore {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        const { items } = get();
        const existing = items.find(i => i.product.id === item.product.id);
        if (existing) {
          set({
            items: items.map(i =>
              i.product.id === item.product.id
                ? { ...i, quantity: i.quantity + item.quantity }
                : i
            )
          });
        } else {
          set({ items: [...items, item] });
        }
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }
        set({
          items: get().items.map(item =>
            item.product.id === productId ? { ...item, quantity } : item
          )
        });
      },

      removeItem: (productId) => {
        set({ items: get().items.filter(item => item.product.id !== productId) });
      },

      clearCart: () => set({ items: [] }),

      getTotalItems: () => get().items.reduce((sum, item) => sum + item.quantity, 0),

      getTotalPrice: () => get().items.reduce((sum, item) => {
        return sum + item.product.price * item.quantity;
      }, 0),
    }),
    {
      name: 'cynthia-cart',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
